'use client';

import { useEffect, useRef, useState } from 'react';
import { useStore } from 'zustand';

import { searchHealthResources } from '@/app/(main)/_components/search-modal/_services/search-health-resources.service';
import { searchHealthResourcesByType } from '@/app/(main)/_components/search-modal/_services/search-health-resources-by-type.service';
import { AllSearchResults, SearchType } from '@/app/(main)/_components/search-modal/search-modal.types';
import {
  clearSearchState,
  consumePreselectedFilter,
  removeSelectedSearch,
  setFetchedResults,
  setSearchTerm,
  useSearchState,
} from '@/store/search/search.store';
import { useDebounceFn } from '@/hooks/use-debounce.hook';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { getLogger } from '@/logger/logger';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

const EMPTY_RESULTS: AllSearchResults = {
  [SearchType.ALL]: { results: [], total: 0 },
  [SearchType.PROCEDURE]: { results: [], total: 0 },
  [SearchType.PROFESSIONAL]: { results: [], total: 0 },
  [SearchType.PARTNER]: { results: [], total: 0 },
};

export function useSearchModalFetch() {
  const searchState = useStore(useSearchState, (state) => state);
  const analytics = getAnalyticsPlugin();

  const chooseInitialResults = (): AllSearchResults =>
    searchState.fetchedResults ?? EMPTY_RESULTS;

  const chooseShouldShowSearchResults = (): boolean =>
    searchState.fetchedResults
      ? Object.values(searchState.fetchedResults).some((r) => r.results.length > 0)
      : false;

  const [inputValue, setInputValue] = useState('');
  const [searchDisplayValue, setSearchDisplayValue] = useState('');
  const [shouldShowSearchResults, setShouldShowSearchResults] = useState(chooseShouldShowSearchResults());
  const [shouldShowEmptyState, setShouldShowEmptyState] = useState(false);
  const [searchResultIsLoading, setSearchResultIsLoading] = useState(false);
  const [allResults, setAllResults] = useState<AllSearchResults>(chooseInitialResults());

  const activeRequestId = useRef(0);
  const debouncedSetInputValue = useDebounceFn(setInputValue, 400);

  useEffect(() => {
    if (searchState.searchTerm) {
      setInputValue(searchState.searchTerm);
      setSearchDisplayValue(searchState.searchTerm);
    }
  }, [searchState.searchTerm]);

  useEffect(() => {
    const currentRequestId = ++activeRequestId.current;

    if (inputValue.length < 3) {
      clearSearchState();
      setAllResults(EMPTY_RESULTS);
      setShouldShowSearchResults(false);
      setShouldShowEmptyState(false);
      setSearchResultIsLoading(false);
      return;
    }

    if (searchState.searchTerm === inputValue) {
      return;
    }

    async function fetchResults() {
      setAllResults(EMPTY_RESULTS);
      setShouldShowSearchResults(false);
      setShouldShowEmptyState(false);
      setSearchResultIsLoading(true);

      try {
        const [all, procedures, professionals, partners] = await Promise.all([
          searchHealthResources(inputValue),
          searchHealthResourcesByType(inputValue, SearchType.PROCEDURE),
          searchHealthResourcesByType(inputValue, SearchType.PROFESSIONAL),
          searchHealthResourcesByType(inputValue, SearchType.PARTNER),
        ]);

        const results: AllSearchResults = {
          [SearchType.ALL]: all,
          [SearchType.PROCEDURE]: procedures,
          [SearchType.PROFESSIONAL]: professionals,
          [SearchType.PARTNER]: partners,
        };

        if (currentRequestId !== activeRequestId.current) {
          return;
        }

        const hasAnyResult = Object.values(results).some((r) => r.total > 0);

        if (hasAnyResult) {
          setShouldShowSearchResults(true);
          setShouldShowEmptyState(false);
        } else {
          setShouldShowEmptyState(true);
        }

        setFetchedResults(results);
        consumePreselectedFilter(results);
        setSearchTerm(inputValue);
        setAllResults(results);
      } catch (error) {
        getLogger('SearchModal - Default').error('Error fetching search results', {
          inputValue,
          currentRequestId,
          error,
        });
        if (currentRequestId === activeRequestId.current) {
          setShouldShowEmptyState(true);
        }
      } finally {
        if (currentRequestId === activeRequestId.current) {
          setSearchResultIsLoading(false);
        }
      }
    }

    fetchResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSetInputValue(e.target.value);
    setSearchDisplayValue(e.target.value);
  };

  const handleRemoveSelectedSearch = () => {
    removeSelectedSearch();
    analytics.track(AnalyticsEnum.CLICK_CLEAR_FILTERS_SEARCH_MODAL);
  };

  return {
    inputValue,
    searchDisplayValue,
    handleSearchInput,
    allResults,
    shouldShowSearchResults,
    shouldShowEmptyState,
    searchResultIsLoading,
    handleRemoveSelectedSearch,
  };
}
